import * as React from "react";
import { useSelector, connect } from "react-redux";
import { uniq, flatten, values } from "lodash";
import { Container } from "semantic-ui-react";
import * as moment from "moment";

import {
    PhysiotherapyReferralReportV2 as PhysiotherapyReferralReportV2Common,
    PhysiotherapyReferralReportDispatchProps
} from "@teamprevent/common/physiotherapyReferralReport/components/PhysiotherapyReferralReportV2";
import {
    PHYSIOTHERAPY_REFERRAL_REPORT_KEY,
    PhysiotherapyReferralReportModel,
    PhysiotherapyReferralReportFormSections as sections,
    PhysiotherapyReferralReportDetails
} from "@teamprevent/common/physiotherapyReferralReport/models";
import { questionnaireLoaded, updateQuestion, submitInvalidQuestionnaire } from "@teamprevent/common/questionnaires/actions";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";
import * as questionIds from "@teamprevent/common/physiotherapyReferralReport/questionIds";
import { masterConfig } from "@teamprevent/common/physiotherapyReferralReport/masterConfig";
import { ConsultationDetails } from "@teamprevent/common/consultations/models";
import { TextSnippetSelector } from "@teamprevent/common/components/TextSnippetSelector";
import { usePrevious } from "@teamprevent/common/customHooks";

import { ApplicationState } from "../../applicationState";
import { AddTaskModel, AddTaskFormModel, TaskType, mapToCreateTaskModel } from "../../task/model";
import {
    getPhysiotherapyReferralReport,
    savePhysiotherapyReferralReport,
    submitPhysiotherapyReferralReportV2,
    loadConsultationAndPhysiotherapyReferralDetails,
    submitPhysiotherapyReferralReportAmendment
} from "../actions";
import { loadEmployeeRecordDetail } from "../../crud/employeeRecord/actions";
import { AddTasksInputs } from "../../tasks/components/addTasksInputs";
import { physiotherapyReferralReportApi } from "../PhysiotherapyReferralReportApi";
import { AdminUser } from "../../crud/adminUser";

interface AdminPhysiotherapyReferralReportOwnProps {
    nextTaskRequired?: boolean;
    isReportAmendment: boolean;
}

interface AdminPhysiotherapyReferralReportDispatchProps {
    submitReport: (ohCaseId: string, taskId: string, tasks: AddTaskModel[], isReportAmendment: boolean) => Promise<void>;
}

type AllProps = AdminPhysiotherapyReferralReportDispatchProps
    & PhysiotherapyReferralReportDispatchProps
    & AdminPhysiotherapyReferralReportOwnProps;

const PhysiotherapyReferralReportV2Unconnected: React.FunctionComponent<AllProps> = (props: AllProps) => {
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [tasks, setTasks] = React.useState<{ task: AddTaskFormModel; isValid: boolean }[]>([]);
    const [reportDetails, setReportDetails] = React.useState<PhysiotherapyReferralReportDetails>(null);

    const currentUserId = useSelector((state: ApplicationState) => state.currentUser?.id);
    const currentUser = useSelector<ApplicationState, AdminUser | undefined>((state: ApplicationState) => state.adminUsers?.find(u => u.id === currentUserId));
    const previousUser = usePrevious(currentUser);
    const customerId = useSelector((state: ApplicationState) => state.router.params?.customerId);
    const ohCaseId = useSelector((state: ApplicationState) => state.router.params?.ohCaseId);
    const taskId = useSelector((state: ApplicationState) => state.router.params?.taskId);
    const ohCase = useSelector((state: ApplicationState) => state.ohCases?.find(c => c.id === ohCaseId));
    const employeeRecord = useSelector((state: ApplicationState) =>
        state.employeeRecordsDetail?.employeeRecords?.find(er => er.id === ohCase?.employeeRecordId));
    const consultationAndReferralDetails: ConsultationDetails = useSelector((state: ApplicationState) =>
        state.consultationDetails.find(r => r.referralReportTaskId === taskId));
    const successorTasks: TaskType[] = consultationAndReferralDetails?.successorTasks;
    const questions = useSelector((state: ApplicationState) =>
        uniq(flatten(state.questionnaires[PHYSIOTHERAPY_REFERRAL_REPORT_KEY]?.sections?.map(section => values(section.questions)))));
    const eTag = useSelector((state: ApplicationState) => state.questionnaires[PHYSIOTHERAPY_REFERRAL_REPORT_KEY]?.eTag);
    const report: PhysiotherapyReferralReportModel = { questions, eTag };
    const task = ohCase?.tasks?.find(t => t.id === taskId);
    const readonly = !task || task.completed;

    React.useEffect(() => {
        if (consultationAndReferralDetails && employeeRecord && ohCase && task && taskId) {
            setIsLoading(false);
        }
    }, [consultationAndReferralDetails, employeeRecord, ohCase, task, taskId]);

    React.useEffect(() => {
        if (currentUserId && currentUserId !== previousUser?.id) {
            const updateReportDetailsSectionAnswer = (questionId: string, questionAnswer: string) => {
                const questionText = masterConfig.find(q => q.id === questionId)?.questionText;
                props.updateAnswer({
                    sectionId: sections.reportDetailsSectionId,
                    id: questionId,
                    question: { question: questionText, answer: questionAnswer, isValid: true }
                });
            };

            const updateCurrentUserAnswers = () => {
                if (!readonly) {
                    updateReportDetailsSectionAnswer(questionIds.CliniciansId, currentUserId);
                    updateReportDetailsSectionAnswer(questionIds.CliniciansName, `${currentUser.firstName} ${currentUser.surname}`);
                    updateReportDetailsSectionAnswer(questionIds.CliniciansQualification, currentUser.qualification);
                    updateReportDetailsSectionAnswer(questionIds.CliniciansEmail, currentUser.emailAddress);
                }
            };

            updateCurrentUserAnswers();
        }
    }, [currentUserId, previousUser?.id]);

    React.useEffect(() => {
        if (consultationAndReferralDetails && employeeRecord && ohCase) {
            setIsLoading(false);
        }
    }, [consultationAndReferralDetails, employeeRecord, ohCase]);

    React.useEffect(() => {
        (async () => {
            if (ohCaseId && taskId && readonly) {
                const details = await physiotherapyReferralReportApi.getReportDetails(ohCaseId, taskId);
                setReportDetails(details);
            }
        })();
    }, [ohCaseId, taskId, readonly]);

    const loadQuestionnaire = async () => {
        await props.loadQuestionnaire(ohCaseId, taskId, customerId);
    };

    const submitQuestionnaire = async (physiotherapyReferralReport: PhysiotherapyReferralReportModel): Promise<boolean> => {
        setSubmitted(true);
        let valid = true;
        if (!physiotherapyReferralReport.questions.every(question => question.isValid)) {
            props.submitInvalidQuestionnaire();
            valid = false;
            return false;
        }
        if (props.nextTaskRequired && tasks.some(t => !t.isValid)) {
            valid = false;
            return false;
        }

        if (valid) {
            const dateAnswerIndex = physiotherapyReferralReport.questions
                .findIndex(q => q.sectionId === sections.reportDetailsSectionId && q.id === questionIds.ReportDate);
            const reportDateQuestion = masterConfig.find(c => c.id === questionIds.ReportDate);
            const dateAnswer = {
                id: reportDateQuestion.id,
                question: reportDateQuestion.questionText,
                answer: moment.utc().format("YYYY-MM-DD HH:mm:ss"),
                isValid: true,
                sectionId: sections.reportDetailsSectionId
            };
            if (dateAnswerIndex > -1) {
                physiotherapyReferralReport.questions[dateAnswerIndex] = dateAnswer;
            } else {
                physiotherapyReferralReport.questions.push(dateAnswer);
            }
            await props.saveReport(ohCaseId, taskId, physiotherapyReferralReport);
            const createTasks = props.nextTaskRequired ? tasks.map(t => mapToCreateTaskModel(t.task, ohCaseId)) : null;
            await props.submitReport(ohCaseId, taskId, createTasks, props.isReportAmendment);
            props.onCompletion();

            return true;
        }

        return false;
    };

    const extraSections = !readonly && successorTasks &&
        props.nextTaskRequired ? [{
            id: "NextTask",
            title: "Further Action being Taken",
            content: (
                <AddTasksInputs
                    taskTypes={successorTasks}
                    onChange={formTasks => setTasks(formTasks)}
                    ohCaseId={ohCaseId}
                    showErrors={submitted}
                    assignmentOptionRequired={true}
                />)
        }] : [];

    const updateAssessmentAndOpinionsQuestion = (value: any) => {
        const assessmentAndOpinionsQuestion = report.questions.find(q => q.id === sections.assessmentAndOpinionsSectionId);
        if (assessmentAndOpinionsQuestion) {
            props.updateAnswer({
                sectionId: assessmentAndOpinionsQuestion.sectionId,
                id: assessmentAndOpinionsQuestion.id,
                question: {
                    ...assessmentAndOpinionsQuestion,
                    answer: (assessmentAndOpinionsQuestion.answer || "") + value
                }
            });
        }
    };

    return (
        <Container className="referral-report">
            {readonly === false && employeeRecord &&
                <h2 className="sticky-header">Physiotherapy Referral Report for {employeeRecord.firstName} {employeeRecord.lastName}</h2>}
            <PhysiotherapyReferralReportV2Common
                readonly={readonly}
                loading={isLoading}
                customerId={customerId}
                ohCaseId={ohCaseId}
                taskId={taskId}
                ohCase={ohCase}
                employeeRecord={employeeRecord}
                report={report}
                consultationAndReferralDetails={consultationAndReferralDetails}
                loadQuestionnaire={loadQuestionnaire}
                submitQuestionnaire={submitQuestionnaire}
                updateAnswer={props.updateAnswer}
                loadEmployee={props.loadEmployee}
                loadConsultationDetails={props.loadConsultationDetails}
                saveReport={props.saveReport}
                clearQuestionnaire={props.clearQuestionnaire}
                onCompletion={props.onCompletion}
                submitInvalidQuestionnaire={props.submitInvalidQuestionnaire}
                extraSections={extraSections}
                extraQuestionContent={readonly ? null : {
                    AssessmentAndOpinions: (
                        <TextSnippetSelector
                            onSelect={updateAssessmentAndOpinionsQuestion}
                        />)
                }}
                reportDetails={reportDetails}
            />
        </Container>);
};

const mapDispatchToProps = (dispatch: any): PhysiotherapyReferralReportDispatchProps & AdminPhysiotherapyReferralReportDispatchProps => {
    return {
        clearQuestionnaire: () => dispatch(questionnaireLoaded({ questions: [], questionnaireKey: PHYSIOTHERAPY_REFERRAL_REPORT_KEY })),
        loadQuestionnaire: (ohCaseId: string, taskId: string) => dispatch(getPhysiotherapyReferralReport(ohCaseId, taskId, PHYSIOTHERAPY_REFERRAL_REPORT_KEY)),
        updateAnswer: payload => dispatch(updateQuestion({ ...payload, questionnaireKey: PHYSIOTHERAPY_REFERRAL_REPORT_KEY })),
        loadEmployee: (employeeRecordId: string, customerId: string) => dispatch(loadEmployeeRecordDetail({ employeeRecordId, customerId })),
        saveReport: (ohCaseId: string, taskId: string, report: PhysiotherapyReferralReportModel) =>
            dispatch(savePhysiotherapyReferralReport(ohCaseId, taskId, report.questions, report.eTag)),
        submitReport: (ohCaseId: string, taskId: string, tasks: AddTaskModel[], isReportAmendment: boolean) =>
        {
            if (isReportAmendment)
            {
                return dispatch(submitPhysiotherapyReferralReportAmendment(ohCaseId, taskId, tasks));
            }
            else
            {
                return dispatch(submitPhysiotherapyReferralReportV2(ohCaseId, taskId, tasks));
            }
        },
        onCompletion: () => dispatch(navigateToParentRoute()),
        submitInvalidQuestionnaire: () => dispatch(submitInvalidQuestionnaire(PHYSIOTHERAPY_REFERRAL_REPORT_KEY)),
        loadConsultationDetails: (ohCaseId: string, taskId: string) => dispatch(loadConsultationAndPhysiotherapyReferralDetails(ohCaseId, taskId))
    };
};

export const PhysiotherapyReferralReportV2 = connect(null, mapDispatchToProps)(PhysiotherapyReferralReportV2Unconnected);
